/**
 * Evaluator for the subset of survey-core expression syntax that `visibleIf`
 * conditions use.
 *
 * This exists instead of a `survey-core` dependency because the whole authored
 * corpus is single equalities against a quoted literal, and survey-core is a
 * complete form runtime — question models, serializer, theming, localization —
 * that registers every built-in question type as an import-time side effect, so
 * it does not tree-shake down to the one condition runner we would use. Some of
 * it also reaches for `document`, which is a poor bargain in a native bundle.
 *
 * The governing rule is **fail open**: anything this module cannot parse or
 * cannot honestly answer leaves the question visible. Hiding on failure would
 * silently delete a question from a live survey, and since a malformed element
 * elsewhere in the schema is already dropped before it reaches here, a strict
 * reading would let one broken question erase a second, unrelated one. An
 * over-shown question is a visible bug; a missing one is not.
 *
 * Deliberately out of scope, and therefore fail-open: arithmetic, function
 * calls such as `iif()`, dotted or indexed paths inside braces, and wildcards.
 */

type Token =
  | { kind: 'ref'; name: string }
  | { kind: 'literal'; value: string | number | boolean }
  | { kind: 'operator'; value: OperatorName }
  | { kind: 'logical'; value: 'and' | 'or' }
  | { kind: 'not' }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'lbracket' }
  | { kind: 'rbracket' }
  | { kind: 'comma' };

type OperatorName =
  | '='
  | '<>'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'contains'
  | 'notcontains'
  | 'anyof'
  | 'allof'
  | 'empty'
  | 'notempty';

type Node =
  | { kind: 'ref'; name: string }
  | { kind: 'literal'; value: string | number | boolean }
  | { kind: 'list'; items: Node[] }
  | { kind: 'not'; operand: Node }
  | { kind: 'logical'; operator: 'and' | 'or'; left: Node; right: Node }
  | { kind: 'comparison'; operator: OperatorName; left: Node; right?: Node };

/** An opaque parsed condition. Only this module reads its shape. */
export type VisibilityCondition = Node;

/**
 * The minimum an element must expose to take part in visibility resolution.
 * Structural rather than an import of the survey element type, because the
 * schema module imports this one — typing it the other way would be a cycle.
 */
export interface VisibilityRule {
  name: string;
  visibleIf?: string;
}

class UnsupportedExpressionError extends Error {}

const SYMBOL_OPERATORS: [string, OperatorName][] = [
  ['<>', '<>'],
  ['!=', '<>'],
  ['>=', '>='],
  ['<=', '<='],
  ['==', '='],
  ['=', '='],
  ['>', '>'],
  ['<', '<'],
];

const WORD_OPERATORS = new Set<string>([
  'contains',
  'notcontains',
  'anyof',
  'allof',
  'empty',
  'notempty',
]);

/** Operators that take no right-hand operand. */
const UNARY_OPERATORS = new Set<OperatorName>(['empty', 'notempty']);

function isDigit(character: string) {
  return character >= '0' && character <= '9';
}

function isWordCharacter(character: string) {
  return /[A-Za-z_]/.test(character);
}

function readQuotedString(expression: string, openingIndex: number) {
  const quote = expression[openingIndex];

  let value = '';

  let position = openingIndex + 1;

  while (position < expression.length) {
    const character = expression[position];

    if (character === '\\' && position + 1 < expression.length) {
      value += expression[position + 1];

      position += 2;

      continue;
    }

    if (character === quote) {
      return { value, nextIndex: position + 1 };
    }

    value += character;

    position += 1;
  }

  throw new UnsupportedExpressionError('Unterminated string literal');
}

function tokenize(expression: string): Token[] {
  const tokens: Token[] = [];

  let position = 0;

  while (position < expression.length) {
    const character = expression[position];

    if (/\s/.test(character)) {
      position += 1;

      continue;
    }

    if (character === '{') {
      const closingIndex = expression.indexOf('}', position);

      if (closingIndex === -1) {
        throw new UnsupportedExpressionError('Unterminated question reference');
      }

      const name = expression.slice(position + 1, closingIndex).trim();

      // A dotted path or an indexed access addresses a matrix or panel cell,
      // which has no equivalent in this client's flat answer map.
      if (name === '' || name.includes('.') || name.includes('[') || name.includes('*')) {
        throw new UnsupportedExpressionError('Unsupported question reference');
      }

      tokens.push({ kind: 'ref', name });

      position = closingIndex + 1;

      continue;
    }

    if (character === "'" || character === '"') {
      const { value, nextIndex } = readQuotedString(expression, position);

      tokens.push({ kind: 'literal', value });

      position = nextIndex;

      continue;
    }

    if (isDigit(character)) {
      let end = position;

      while (end < expression.length && (isDigit(expression[end]) || expression[end] === '.')) {
        end += 1;
      }

      tokens.push({ kind: 'literal', value: Number(expression.slice(position, end)) });

      position = end;

      continue;
    }

    if (character === '(') {
      tokens.push({ kind: 'lparen' });

      position += 1;

      continue;
    }

    if (character === ')') {
      tokens.push({ kind: 'rparen' });

      position += 1;

      continue;
    }

    if (character === '[') {
      tokens.push({ kind: 'lbracket' });

      position += 1;

      continue;
    }

    if (character === ']') {
      tokens.push({ kind: 'rbracket' });

      position += 1;

      continue;
    }

    if (character === ',') {
      tokens.push({ kind: 'comma' });

      position += 1;

      continue;
    }

    if (character === '&' || character === '|') {
      const pair = expression.slice(position, position + 2);

      if (pair !== '&&' && pair !== '||') {
        throw new UnsupportedExpressionError('Unsupported operator');
      }

      tokens.push({ kind: 'logical', value: pair === '&&' ? 'and' : 'or' });

      position += 2;

      continue;
    }

    // Checked before the symbol table so "!=" is not read as a negation.
    if (character === '!' && expression[position + 1] !== '=') {
      tokens.push({ kind: 'not' });

      position += 1;

      continue;
    }

    const symbolOperator = SYMBOL_OPERATORS.find(([symbol]) =>
      expression.startsWith(symbol, position)
    );

    if (symbolOperator) {
      tokens.push({ kind: 'operator', value: symbolOperator[1] });

      position += symbolOperator[0].length;

      continue;
    }

    if (isWordCharacter(character)) {
      let end = position;

      while (end < expression.length && isWordCharacter(expression[end])) {
        end += 1;
      }

      const word = expression.slice(position, end).toLowerCase();

      position = end;

      if (word === 'and' || word === 'or') {
        tokens.push({ kind: 'logical', value: word });

        continue;
      }

      if (word === 'not') {
        tokens.push({ kind: 'not' });

        continue;
      }

      if (word === 'true' || word === 'false') {
        tokens.push({ kind: 'literal', value: word === 'true' });

        continue;
      }

      if (WORD_OPERATORS.has(word)) {
        tokens.push({ kind: 'operator', value: word as OperatorName });

        continue;
      }

      // A bare identifier is a function name, which is out of scope.
      throw new UnsupportedExpressionError('Unsupported identifier');
    }

    throw new UnsupportedExpressionError('Unsupported character');
  }

  return tokens;
}

/** Recursive descent, with `or` binding looser than `and` as in ordinary boolean logic. */
function parseTokens(tokens: Token[]): Node {
  let position = 0;

  function peek(): Token | undefined {
    return tokens[position];
  }

  function parseOperand(): Node {
    const token = peek();

    if (!token) {
      throw new UnsupportedExpressionError('Unexpected end of expression');
    }

    if (token.kind === 'ref') {
      position += 1;

      return { kind: 'ref', name: token.name };
    }

    if (token.kind === 'literal') {
      position += 1;

      return { kind: 'literal', value: token.value };
    }

    if (token.kind === 'lbracket') {
      position += 1;

      const items: Node[] = [];

      while (peek() && peek()?.kind !== 'rbracket') {
        items.push(parseOperand());

        if (peek()?.kind === 'comma') {
          position += 1;
        }
      }

      if (peek()?.kind !== 'rbracket') {
        throw new UnsupportedExpressionError('Unterminated list literal');
      }

      position += 1;

      return { kind: 'list', items };
    }

    throw new UnsupportedExpressionError('Expected an operand');
  }

  function parsePrimary(): Node {
    const token = peek();

    if (token?.kind === 'not') {
      position += 1;

      return { kind: 'not', operand: parsePrimary() };
    }

    if (token?.kind === 'lparen') {
      position += 1;

      const inner = parseOr();

      if (peek()?.kind !== 'rparen') {
        throw new UnsupportedExpressionError('Unbalanced parentheses');
      }

      position += 1;

      return inner;
    }

    const left = parseOperand();

    const operatorToken = peek();

    if (operatorToken?.kind !== 'operator') {
      return left;
    }

    position += 1;

    if (UNARY_OPERATORS.has(operatorToken.value)) {
      return { kind: 'comparison', operator: operatorToken.value, left };
    }

    return { kind: 'comparison', operator: operatorToken.value, left, right: parseOperand() };
  }

  function parseAnd(): Node {
    let left = parsePrimary();

    for (let token = peek(); token?.kind === 'logical' && token.value === 'and'; token = peek()) {
      position += 1;

      left = { kind: 'logical', operator: 'and', left, right: parsePrimary() };
    }

    return left;
  }

  function parseOr(): Node {
    let left = parseAnd();

    for (let token = peek(); token?.kind === 'logical' && token.value === 'or'; token = peek()) {
      position += 1;

      left = { kind: 'logical', operator: 'or', left, right: parseAnd() };
    }

    return left;
  }

  const root = parseOr();

  if (position !== tokens.length) {
    throw new UnsupportedExpressionError('Trailing tokens in expression');
  }

  return root;
}

/** Returns `undefined` for anything outside the supported subset, per the fail-open rule. */
export function parseVisibilityCondition(expression: string): VisibilityCondition | undefined {
  try {
    return parseTokens(tokenize(expression));
  } catch {
    return undefined;
  }
}

export function getConditionQuestionNames(condition: VisibilityCondition): string[] {
  const names: string[] = [];

  function walk(node: Node) {
    if (node.kind === 'ref') {
      names.push(node.name);

      return;
    }

    if (node.kind === 'list') {
      node.items.forEach(walk);

      return;
    }

    if (node.kind === 'not') {
      walk(node.operand);

      return;
    }

    if (node.kind === 'logical') {
      walk(node.left);

      walk(node.right);

      return;
    }

    if (node.kind === 'comparison') {
      walk(node.left);

      if (node.right) {
        walk(node.right);
      }
    }
  }

  walk(condition);

  return Array.from(new Set(names));
}

function isEmptyValue(value: unknown) {
  if (value === null || value === undefined) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return typeof value === 'string' && value.trim() === '';
}

function toComparableText(value: unknown) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function isNumericValue(value: unknown) {
  return (
    typeof value === 'number' ||
    (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)))
  );
}

/**
 * Equality as survey-core compares by default: trimmed and case-insensitive.
 *
 * The numeric pairing matters because the same condition is evaluated at two
 * different stages — against raw form values, where a numeric answer is still
 * the string the respondent typed, and against parsed output, where it is a
 * number. Comparing those numerically is what keeps the two stages agreeing.
 */
function valuesAreEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) {
      return false;
    }

    return (
      left.length === right.length &&
      left.every((item, index) => valuesAreEqual(item, right[index]))
    );
  }

  if (isEmptyValue(left) || isEmptyValue(right)) {
    return isEmptyValue(left) && isEmptyValue(right);
  }

  if (isNumericValue(left) && isNumericValue(right)) {
    return Number(left) === Number(right);
  }

  return toComparableText(left) === toComparableText(right);
}

function compareOrdered(
  left: unknown,
  right: unknown,
  compare: (first: number, second: number) => boolean
) {
  if (
    isEmptyValue(left) ||
    isEmptyValue(right) ||
    !isNumericValue(left) ||
    !isNumericValue(right)
  ) {
    return false;
  }

  return compare(Number(left), Number(right));
}

function containsValue(left: unknown, right: unknown) {
  if (isEmptyValue(left)) {
    return false;
  }

  if (Array.isArray(left)) {
    return left.some((item) => valuesAreEqual(item, right));
  }

  if (typeof left === 'string' && typeof right === 'string') {
    return toComparableText(left).includes(toComparableText(right));
  }

  return valuesAreEqual(left, right);
}

function toItems(value: unknown) {
  return Array.isArray(value) ? value : [value];
}

function resolve(node: Node, values: Record<string, unknown>): unknown {
  if (node.kind === 'ref') {
    return values[node.name];
  }

  if (node.kind === 'literal') {
    return node.value;
  }

  if (node.kind === 'list') {
    return node.items.map((item) => resolve(item, values));
  }

  return evaluateNode(node, values);
}

function evaluateNode(node: Node, values: Record<string, unknown>): boolean {
  if (node.kind === 'not') {
    return !evaluateNode(node.operand, values);
  }

  if (node.kind === 'logical') {
    return node.operator === 'and'
      ? evaluateNode(node.left, values) && evaluateNode(node.right, values)
      : evaluateNode(node.left, values) || evaluateNode(node.right, values);
  }

  if (node.kind === 'comparison') {
    const left = resolve(node.left, values);

    if (node.operator === 'empty') {
      return isEmptyValue(left);
    }

    if (node.operator === 'notempty') {
      return !isEmptyValue(left);
    }

    const right = resolve(node.right as Node, values);

    switch (node.operator) {
      case '=':
        return valuesAreEqual(left, right);
      case '<>':
        return !valuesAreEqual(left, right);
      case '>':
        return compareOrdered(left, right, (first, second) => first > second);
      case '>=':
        return compareOrdered(left, right, (first, second) => first >= second);
      case '<':
        return compareOrdered(left, right, (first, second) => first < second);
      case '<=':
        return compareOrdered(left, right, (first, second) => first <= second);
      case 'contains':
        return containsValue(left, right);
      case 'notcontains':
        return !containsValue(left, right);
      case 'anyof':
        return toItems(right).some((item) => containsValue(left, item));
      case 'allof':
        return toItems(right).every((item) => containsValue(left, item));
    }
  }

  const value = resolve(node, values);

  return typeof value === 'boolean' ? value : !isEmptyValue(value);
}

export function evaluateVisibilityCondition(
  condition: VisibilityCondition,
  values: Record<string, unknown>
): boolean {
  try {
    return evaluateNode(condition, values);
  } catch {
    return true;
  }
}

function getParsedRules(rules: VisibilityRule[]) {
  return rules.flatMap((rule) => {
    if (typeof rule.visibleIf !== 'string') {
      return [];
    }

    const condition = parseVisibilityCondition(rule.visibleIf);

    return condition ? [{ name: rule.name, condition }] : [];
  });
}

/** Every question name any condition reads, for subscribing to just those answers. */
export function getVisibilityDependencyNames(rules: VisibilityRule[]): string[] {
  return Array.from(
    new Set(getParsedRules(rules).flatMap((rule) => getConditionQuestionNames(rule.condition)))
  );
}

/** Rules carrying an expression outside the supported subset, for reporting. */
export function getUnsupportedVisibilityRules(rules: VisibilityRule[]): VisibilityRule[] {
  return rules.filter(
    (rule) =>
      typeof rule.visibleIf === 'string' && parseVisibilityCondition(rule.visibleIf) === undefined
  );
}

function areSameNames(first: Set<string>, second: Set<string>) {
  return first.size === second.size && Array.from(first).every((name) => second.has(name));
}

/**
 * Resolves which elements are hidden by their own conditions, given the answers.
 *
 * A hidden question's answer is withheld from every other condition, so a
 * question conditioned on a hidden question hides too, matching survey-core's
 * treatment of a hidden value as cleared. Each pass recomputes the whole hidden
 * set from the original answers with the currently-hidden names removed, which
 * makes the result independent of declaration order — a condition may point at a
 * question declared after it.
 *
 * **The loop bound is the termination guarantee, not an optimization.** A chain
 * settles in as many passes as it is deep, but a pair of mutually negating
 * conditions oscillates forever; the bound stops it with whatever set it last
 * held rather than hanging the form.
 *
 * A condition naming a question this survey cannot answer is left visible
 * instead of resolved, per the fail-open rule — a renamed question must not
 * silently delete its dependent.
 *
 * `answerableNames` is what makes that rule hold for an element that exists but
 * can never carry an answer, such as a display block or a question type this
 * client cannot render. Without it those names look resolvable, and a condition
 * reading one would evaluate against a permanently absent value and resolve to
 * hidden — the exact inversion of the fail-open rule. It defaults to every rule
 * name, which is correct only when every rule can hold an answer.
 *
 * Callers may pass a subset of the answers, as long as it covers every name the
 * rules reference. Note the corollary: a hidden question's answer is still
 * present in `values`, which is exactly why conditions are evaluated against a
 * copy with hidden names cleared rather than against `values` itself.
 */
export function getHiddenElementNames(
  rules: VisibilityRule[],
  values: Record<string, unknown>,
  answerableNames?: Set<string>
): Set<string> {
  const parsedRules = getParsedRules(rules);

  if (parsedRules.length === 0) {
    return new Set<string>();
  }

  const knownNames = answerableNames ?? new Set(rules.map((rule) => rule.name));

  const resolvableRules = parsedRules.filter((rule) =>
    getConditionQuestionNames(rule.condition).every((name) => knownNames.has(name))
  );

  let hiddenNames = new Set<string>();

  for (let pass = 0; pass <= rules.length; pass += 1) {
    const effectiveValues = { ...values };

    hiddenNames.forEach((name) => delete effectiveValues[name]);

    const nextHiddenNames = new Set(
      resolvableRules
        .filter((rule) => !evaluateVisibilityCondition(rule.condition, effectiveValues))
        .map((rule) => rule.name)
    );

    if (areSameNames(nextHiddenNames, hiddenNames)) {
      return hiddenNames;
    }

    hiddenNames = nextHiddenNames;
  }

  return hiddenNames;
}
